import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { TONES, type ListingTone } from './tones'

export interface ListingTag {
  label: string
  tone: ListingTone
}

interface ListingCardProps {
  /** In-page anchors ("#...") render a plain link; anything else a router Link. */
  href: string
  /** Usually a <HalftoneCover>. Its `.listing-cover-art` layer zooms on hover. */
  cover: React.ReactNode
  title: string
  /** Visual size from the layout spec: h5 for story cards, h6 for the denser 4:3 grid. */
  size?: 'h5' | 'h6'
  tags: ListingTag[]
  /** Tags under the title (works grid) or above it (blog grid). */
  tagsPosition?: 'above' | 'below'
  excerpt: string
  excerptLines?: 2 | 3
  className?: string
}

export function ListingTagBox({ tag }: { tag: ListingTag }) {
  const spec = TONES[tag.tone]
  return (
    <span
      className="inline-block px-1.5 py-[0.2em] font-[family-name:var(--font-landing-subdisplay)] text-[0.6875rem] font-bold uppercase leading-none tracking-[0.02em]"
      style={{ backgroundColor: spec.bg, color: spec.fg }}
    >
      {tag.label}
    </span>
  )
}

/**
 * Listing card in the reference's works/blog grid: generated cover, uppercase
 * display title, coloured tag boxes and a clamped excerpt. On hover the card
 * settles to 0.98 while its cover art zooms to 1.15 (600ms, ease-out).
 */
export function ListingCard({
  href,
  cover,
  title,
  size = 'h5',
  tags,
  tagsPosition = 'below',
  excerpt,
  excerptLines = 3,
  className,
}: ListingCardProps) {
  const tagRow = tags.length > 0 && (
    <div className="flex flex-wrap gap-1.5">
      {tags.map((tag) => (
        <ListingTagBox key={tag.label} tag={tag} />
      ))}
    </div>
  )

  const body = (
    <>
      {cover}
      <div className="mt-4 flex flex-col gap-3">
        {tagsPosition === 'above' && tagRow}
        <h3
          className={cn(
            'font-[family-name:var(--font-landing-display)] uppercase tracking-[-0.02em] leading-[1.45] text-[var(--color-landing-gray-900)] dark:text-white text-balance transition-colors duration-200',
            'group-hover:text-[var(--color-landing-accent)]',
            size === 'h5' ? 'text-[1.0625rem] md:text-[1.125rem]' : 'text-[0.9375rem] md:text-base'
          )}
        >
          {title}
        </h3>
        {tagsPosition === 'below' && tagRow}
        <p
          className={cn(
            'landing-text-body-sm text-pretty text-[var(--color-landing-gray-900)]/65 dark:text-white/65',
            excerptLines === 3 ? 'line-clamp-3' : 'line-clamp-2'
          )}
        >
          {excerpt}
        </p>
      </div>
    </>
  )

  const linkClass = cn(
    'group block origin-center transition-transform duration-[600ms] ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
    'hover:scale-[0.98] hover:[&_.listing-cover-art]:scale-[1.15]',
    'focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--color-landing-accent)]',
    className
  )

  return href.startsWith('#') ? (
    <a href={href} className={linkClass}>
      {body}
    </a>
  ) : (
    <Link to={href} className={linkClass}>
      {body}
    </Link>
  )
}
